import React from 'react';
import { Platform, View } from 'react-native';

import {
  ActionSheet,
  Button,
  IconLocationFill,
  Text,
  XStack,
  YStack,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';

import { NoDeliveryRestaurantsModalProps } from './types';

const TITLE = 'No Delivery Restaurants Nearby';
const BODY =
  "We couldn't find any restaurants that deliver to this address. Try another address or switch to pickup.";
const BODY_ID = 'store-locator-delivery-no-restaurants-description';

export const NoDeliveryRestaurantsModal: React.VFC<NoDeliveryRestaurantsModalProps> = ({
  isVisible,
  address,
  onSwitchToPickup,
  onChangeAddress,
  onClose,
}) => {
  const styles = useMqStyles();
  const isDesktop = useMqSelect({ $gteDesktop: true }, false);

  // The web drawer calls `onClose` whenever `isVisible` is false, including on mount, so the
  // sheet only exists while it is shown.
  if (!isVisible) {
    return null;
  }

  return (
    <ActionSheet
      isVisible
      onClose={onClose}
      onRequestClose={onClose}
      sheetAccessibilityLabel={TITLE}
      sheetAriaDescribedBy={BODY_ID}
      hideHeader
      grabber
      isScrollableDrawer
      footer={
        <YStack style={styles.footer}>
          <Button
            size="xl"
            type="solid"
            onPress={onSwitchToPickup}
            style={styles.actionButton}
            testID="store-locator-delivery-no-restaurants-switch-to-pickup"
          >
            <Button.Text>Switch to Pickup</Button.Text>
          </Button>
          <Button
            size="xl"
            type="outline"
            onPress={onChangeAddress}
            style={styles.actionButton}
            testID="store-locator-delivery-no-restaurants-change-address"
          >
            <Button.Text>Change Delivery Address</Button.Text>
          </Button>
        </YStack>
      }
    >
      <YStack style={styles.content} testID="store-locator-delivery-no-restaurants-modal">
        <YStack style={styles.intro}>
          <Text.Heading type={isDesktop ? 'two' : 'one'} style={styles.introText}>
            {TITLE}
          </Text.Heading>
          <Text.Paragraph
            nativeID={BODY_ID}
            size={isDesktop ? 'md' : 'lg'}
            style={styles.introText}
          >
            {BODY}
          </Text.Paragraph>
        </YStack>

        {address ? (
          <YStack style={styles.addressSection}>
            <Text.Paragraph size="md" weight="semibold">
              Your Address
            </Text.Paragraph>
            <XStack
              style={styles.addressCard}
              testID="store-locator-delivery-no-restaurants-address"
            >
              <View aria-hidden style={styles.addressIcon}>
                <IconLocationFill size={24} color={tokens.colors.$houseDark} />
              </View>
              <YStack style={styles.addressLines}>
                <Text.Paragraph size="md" weight="semibold">
                  {address.mainText}
                </Text.Paragraph>
                {address.secondaryText ? (
                  <Text.Paragraph size="sm" style={styles.addressSecondary}>
                    {address.secondaryText}
                  </Text.Paragraph>
                ) : null}
              </YStack>
            </XStack>
          </YStack>
        ) : null}
      </YStack>
    </ActionSheet>
  );
};

const useMqStyles = createMqStyles({
  content: {
    $base: {
      gap: 24,
      paddingBottom: 24,
      paddingHorizontal: 16,
      paddingTop: 24,
    },
    // The desktop modal already spaces its footer 28 below the content.
    $gteDesktop: {
      gap: 28,
      paddingBottom: 0,
      paddingHorizontal: 28,
      paddingTop: 28,
    },
  },
  intro: {
    $base: {
      gap: 16,
    },
    $gteDesktop: {
      gap: 28,
    },
  },
  introText: {
    $base: {
      textAlign: 'center',
    },
    $gteDesktop: {
      textAlign: 'left',
    },
  },
  addressSection: {
    $base: {
      gap: 4,
    },
  },
  addressCard: {
    $base: {
      alignItems: 'center',
      borderColor: tokens.colors.$blackOpacity10,
      borderRadius: 8,
      borderWidth: 1,
      gap: 8,
      paddingHorizontal: 12,
      paddingVertical: 16,
    },
  },
  addressIcon: {
    $base: {
      alignItems: 'center',
      backgroundColor: tokens.colors.$blackOpacity04,
      borderRadius: 20,
      height: 40,
      justifyContent: 'center',
      width: 40,
    },
  },
  addressLines: {
    $base: {
      flex: 1,
      gap: 2,
    },
  },
  addressSecondary: {
    $base: {
      color: tokens.colors.$blackOpacity75,
    },
  },
  footer: {
    $base: {
      backgroundColor: tokens.colors.$white,
      gap: 8,
      // Native's drawer already pads the bottom safe area beneath the footer; web's adds nothing.
      paddingBottom: Platform.OS === 'web' ? 16 : 8,
      paddingHorizontal: 16,
      paddingTop: 16,
    },
    $ltDesktop: {
      shadowColor: tokens.colors.$black,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.08,
      shadowRadius: 12,
    },
    $gteDesktop: {
      paddingBottom: 28,
      paddingHorizontal: 28,
      paddingTop: 0,
    },
  },
  actionButton: {
    $base: {
      width: '100%',
    },
  },
});
